import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabaseHelpers } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { settingsStore } from '../../../lib/localSettingsStore';
import { useToast } from '../../../contexts/ToastContext';

const AddPrinterDrawer = ({ isOpen, onClose, onSave, editingPrinter = null }) => {
  const { user } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [globalSuppliers, setGlobalSuppliers] = useState([]);
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isAddModelModalOpen, setIsAddModelModalOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [newModelName, setNewModelName] = useState('');

  // Listas padrão para fallback e melhor UX
  const COMMON_MANUFACTURERS = [
    { value: 'creality', label: 'Creality' },
    { value: 'prusa_research', label: 'Prusa Research' },
    { value: 'bambu_lab', label: 'Bambu Lab' },
    { value: 'anycubic', label: 'Anycubic' },
    { value: 'elegoo', label: 'Elegoo' }
  ];

  const COMMON_MODELS_MAP = {
    creality: [
      { id: 'ender-3', name: 'Ender 3', build_volume_x: 220, build_volume_y: 220, build_volume_z: 250 },
      { id: 'ender-3-v2', name: 'Ender 3 V2', build_volume_x: 220, build_volume_y: 220, build_volume_z: 250 },
      { id: 'ender-3-s1', name: 'Ender 3 S1', build_volume_x: 220, build_volume_y: 220, build_volume_z: 270 },
      { id: 'cr-10', name: 'CR-10', build_volume_x: 300, build_volume_y: 300, build_volume_z: 400 }
    ],
    prusa_research: [
      { id: 'mk3s-plus', name: 'Prusa i3 MK3S+', build_volume_x: 250, build_volume_y: 210, build_volume_z: 210 },
      { id: 'mini-plus', name: 'Prusa MINI+', build_volume_x: 180, build_volume_y: 180, build_volume_z: 180 },
      { id: 'xl', name: 'Prusa XL', build_volume_x: 360, build_volume_y: 360, build_volume_z: 360 }
    ],
    bambu_lab: [
      { id: 'x1c', name: 'X1 Carbon', build_volume_x: 256, build_volume_y: 256, build_volume_z: 256 },
      { id: 'p1p', name: 'P1P', build_volume_x: 256, build_volume_y: 256, build_volume_z: 256 },
      { id: 'a1', name: 'A1', build_volume_x: 256, build_volume_y: 256, build_volume_z: 256 },
      { id: 'a1-mini', name: 'A1 Mini', build_volume_x: 180, build_volume_y: 180, build_volume_z: 180 }
    ],
    anycubic: [
      { id: 'kobra-2', name: 'Kobra 2', build_volume_x: 260, build_volume_y: 260, build_volume_z: 220 },
      { id: 'mega-s', name: 'Mega S', build_volume_x: 210, build_volume_y: 210, build_volume_z: 205 }
    ],
    elegoo: [
      { id: 'neptune-3', name: 'Neptune 3', build_volume_x: 225, build_volume_y: 225, build_volume_z: 280 },
      { id: 'neptune-4', name: 'Neptune 4', build_volume_x: 225, build_volume_y: 225, build_volume_z: 265 }
    ]
  };

  const [formData, setFormData] = useState({
    name: editingPrinter?.name || '',
    manufacturer_id: editingPrinter?.manufacturer_id || '',
    model_id: editingPrinter?.model_id || '',
    custom_model: editingPrinter?.model || '',
    custom_manufacturer: editingPrinter?.brand || '',
    supplier: editingPrinter?.supplier || '',
    build_volume_x: editingPrinter?.build_volume_x || '',
    build_volume_y: editingPrinter?.build_volume_y || '',
    build_volume_z: editingPrinter?.build_volume_z || '',
    power_consumption: editingPrinter?.power_consumption || editingPrinter?.powerConsumption || '',
    hourly_rate: editingPrinter?.hourly_rate || '15.00',
    status: editingPrinter?.status || 'active',
    image_urls: Array.isArray(editingPrinter?.image_urls) ? editingPrinter?.image_urls : (editingPrinter?.image ? [editingPrinter?.image] : []),
    notes: editingPrinter?.notes || '',
    purchase_price: editingPrinter?.purchasePrice || '',
    purchase_date: editingPrinter?.purchaseDate || '',
    has_ams: editingPrinter?.hasAMS || false,
    nozzle_size: editingPrinter?.nozzleSize || '',
    custom_nozzle_size: '',
    filament_printed_kg: editingPrinter?.filamentPrintedKg || '',
    // Ajustes avançados
    filament_printed_brl: editingPrinter?.totalFilamentBRL || '',
    avg_filament_per_job_kg: editingPrinter?.avgFilamentPerJobKg || '',
    avg_filament_per_job_brl: editingPrinter?.avgFilamentPerJobBRL || '',
    failure_rate_pct: editingPrinter?.failureRatePct || '',
    material_waste_kg: editingPrinter?.materialWasteKg || '',
    material_waste_brl: editingPrinter?.materialWasteBRL || '',
    gross_profit_brl: editingPrinter?.grossProfit || '',
    net_profit_brl: editingPrinter?.netProfit || ''
  });

  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: 'active', label: 'Ativa' },
    { value: 'idle', label: 'Ociosa' },
    { value: 'maintenance', label: 'Em manutenção' },
    { value: 'inactive', label: 'Fora de serviço' },
    { value: 'nonexistent', label: 'Inexistente (vendida/retirada)' }
  ];

  // Load manufacturers and global suppliers on drawer open
  useEffect(() => {
    if (isOpen) {
      loadManufacturers();
      (async () => {
        const { data } = await settingsStore.getSuppliers();
        setGlobalSuppliers(Array.isArray(data) ? data : []);
      })();
    }
  }, [isOpen]);

  // Load models when manufacturer changes
  useEffect(() => {
    if (formData?.manufacturer_id) {
      loadModels(formData?.manufacturer_id);
    } else {
      setModels([]);
      setSelectedModel(null);
    }
  }, [formData?.manufacturer_id]);

  // Auto-fill build volume when model is selected
  useEffect(() => {
    if (formData?.model_id && models?.length > 0) {
      const model = models?.find(m => m?.value === formData?.model_id);
      if (model) {
        setSelectedModel(model);
        setFormData(prev => ({
          ...prev,
          build_volume_x: model?.build_volume_x,
          build_volume_y: model?.build_volume_y,
          build_volume_z: model?.build_volume_z,
          custom_model: model?.name || model?.value || model?.label
        }));
      }
    }
  }, [formData?.model_id, models]);

  const loadManufacturers = async () => {
    try {
      // 1) Supabase manufacturers
      const { data: supaManufacturers, error: supaError } = await supabaseHelpers.getPrinterManufacturers();
      const supaOpts = Array.isArray(supaManufacturers)
        ? supaManufacturers.map(m => ({ value: m?.id, label: m?.name }))
        : [];

      // 2) Local settings fallback
      let settingsOpts = [];
      try {
        const { data } = await settingsStore.getPrinterBrands();
        const brandsFromSettings = Array.isArray(data) ? data : [];
        settingsOpts = brandsFromSettings
          .filter(Boolean)
          .map(label => ({ value: String(label).toLowerCase().replace(/\s+/g, '_'), label: String(label) }));
      } catch (err) {
        console.error('Erro ao carregar fabricantes (settings):', err);
      }

      // 3) Common list fallback
      const commonOpts = [...COMMON_MANUFACTURERS];

      // 4) Merge with de-dup by label (case-insensitive), preferring Supabase entries
      const merged = [...supaOpts, ...settingsOpts, ...commonOpts];
      const uniqueByLabel = Array.from(new Map(merged.map(m => [String(m?.label || '').toLowerCase(), m])).values())
        .filter(Boolean)
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));

      uniqueByLabel.push({ value: 'custom', label: '+ Adicionar Novo Fabricante' });
      setManufacturers(uniqueByLabel);
      if (supaError) {
        console.warn('Aviso ao carregar fabricantes (Supabase):', supaError);
      }
    } catch (error) {
      console.error('Erro ao carregar fabricantes:', error);
      const fallback = [...COMMON_MANUFACTURERS];
      fallback.push({ value: 'custom', label: '+ Adicionar Novo Fabricante' });
      setManufacturers(fallback);
    }
  };

  const loadModels = async (manufacturerId) => {
    if (manufacturerId === 'custom') {
      setModels([]);
      return;
    }
    setIsLoadingModels(true);
    try {
      // 1) Supabase models for selected manufacturer (UUID or slug)
      const { data: supaModels, error: supaError } = await supabaseHelpers.getPrinterModelsByManufacturer(manufacturerId);
      const supaOpts = Array.isArray(supaModels)
        ? supaModels.map(m => ({
            value: m?.id,
            label: m?.name && (m?.build_volume_x && m?.build_volume_y && m?.build_volume_z)
              ? `${m.name} (${m.build_volume_x}×${m.build_volume_y}×${m.build_volume_z}mm)`
              : m?.name,
            id: m?.id,
            name: m?.name,
            build_volume_x: m?.build_volume_x,
            build_volume_y: m?.build_volume_y,
            build_volume_z: m?.build_volume_z
          }))
        : [];

      // 2) Local settings fallback by manufacturer label
      const manufacturerLabel = manufacturers?.find(m => m?.value === manufacturerId)?.label || manufacturerId;
      let settingsOpts = [];
      try {
        const { data } = await settingsStore.getPrinterModelsByBrand(manufacturerLabel);
        const modelsFromSettings = Array.isArray(data) ? data : [];
        // Attempt to enrich from common map if available
        const key = String(manufacturerId || manufacturerLabel || '').trim().toLowerCase();
        const commonList = COMMON_MODELS_MAP[key] || [];
        settingsOpts = modelsFromSettings.map(name => {
          const match = commonList.find(m => String(m.name).toLowerCase() === String(name).toLowerCase());
          if (match) {
            return {
              value: match.name,
              label: `${match.name} (${match.build_volume_x}×${match.build_volume_y}×${match.build_volume_z}mm)`,
              ...match
            };
          }
          return { value: name, label: name, name };
        });
      } catch (err) {
        console.error('Erro ao carregar modelos (settings):', err);
      }

      // 3) Common models fallback
      const key = String(manufacturerId || manufacturerLabel || '').trim().toLowerCase();
      const commonList = COMMON_MODELS_MAP[key] || [];
      const commonOpts = commonList.map(m => ({
        value: m.name,
        label: `${m.name} (${m.build_volume_x}×${m.build_volume_y}×${m.build_volume_z}mm)`,
        ...m
      }));

      // 4) Merge and dedup by label, preferring Supabase model entries
      const merged = [...supaOpts, ...settingsOpts, ...commonOpts];
      const uniqueByLabel = Array.from(new Map(merged.map(m => [String(m?.label || '').toLowerCase(), m])).values());
      uniqueByLabel.push({ value: 'custom', label: '+ Adicionar Novo Modelo' });
      setModels(uniqueByLabel);
      if (supaError) {
        console.warn('Aviso ao carregar modelos (Supabase):', supaError);
      }
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
      const key = String(manufacturerId || '').trim().toLowerCase();
      const commonList = COMMON_MODELS_MAP[key] || [];
      const commonOpts = commonList.map(m => ({ value: m.name, label: `${m.name} (${m.build_volume_x}×${m.build_volume_y}×${m.build_volume_z}mm)`, ...m }));
      setModels([...commonOpts, { value: 'custom', label: '+ Adicionar Novo Modelo' }]);
    } finally {
      setIsLoadingModels(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user types
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Handle power consumption masking (numbers only)
    if (field === 'power_consumption') {
      const numericValue = value?.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [field]: numericValue }));
    }

    // Build volume X/Y/Z numeric-only
    if (/^build_volume_[xyz]$/.test(field)) {
      const numericValue = String(value || '')?.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [field]: numericValue }));
    }

    // Mask BRL currency for purchase price
    if (field === 'purchase_price') {
      const digits = String(value || '')?.replace(/\D/g, '');
      const num = Number(digits) / 100;
      const masked = num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      setFormData(prev => ({ ...prev, [field]: masked }));
    }

    // Currency masks for advanced fields
    const currencyFields = [
      'filament_printed_brl',
      'avg_filament_per_job_brl',
      'material_waste_brl',
      'gross_profit_brl',
      'net_profit_brl'
    ];
    if (currencyFields.includes(field)) {
      const digits = String(value || '')?.replace(/\D/g, '');
      const num = Number(digits) / 100;
      const masked = num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      setFormData(prev => ({ ...prev, [field]: masked }));
    }

    // Formatação rápida: 0.00 para kg (mantém apenas números e 2 casas decimais)
    if (field === 'filament_printed_kg') {
      const raw = String(value || '')?.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('').slice(0,2)}` : parts[0];
      setFormData(prev => ({ ...prev, filament_printed_kg: normalized }));
    }

    // Decimal masks for advanced kg and percentage fields (2 decimals)
    const dec2Fields = [
      'avg_filament_per_job_kg',
      'material_waste_kg',
      'failure_rate_pct'
    ];
    if (dec2Fields.includes(field)) {
      const raw = String(value || '')?.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('').slice(0,2)}` : parts[0];
      setFormData(prev => ({ ...prev, [field]: normalized }));
    }

    // Máscara para taxa/hora: mantém decimais, 2 casas
    if (field === 'hourly_rate') {
      const raw = String(value || '')?.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('').slice(0,2)}` : parts[0];
      setFormData(prev => ({ ...prev, hourly_rate: normalized }));
    }

    // Máscara para bico personalizado: decimal curto, até 3 casas
    if (field === 'custom_nozzle_size') {
      const raw = String(value || '')?.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('').slice(0,3)}` : parts[0];
      setFormData(prev => ({ ...prev, custom_nozzle_size: normalized }));
    }
  };

  // Entrada única para volume L×P×A; aceita separadores x, ×, *, espaço
  const handleVolumeCombinedChange = (value) => {
    const raw = String(value || '').toLowerCase();
    const tokens = raw.replace(/mm/gi, '')
      .replace(/[^0-9x×*\s.]/g, '')
      .replace(/\s+/g, ' ')
      .split(/[x×*\s]/)
      .filter(Boolean);
    const [x, y, z] = tokens.map(t => parseInt(t, 10)).filter(n => !isNaN(n));
    setFormData(prev => ({
      ...prev,
      build_volume_x: x || '',
      build_volume_y: y || '',
      build_volume_z: z || ''
    }));
    if (errors?.build_volume_x || errors?.build_volume_y || errors?.build_volume_z) {
      setErrors(prev => ({ ...prev, build_volume_x: null, build_volume_y: null, build_volume_z: null }));
    }
  };

  const handleFilesUpload = async (files) => {
    if (!user?.id) return;
    if (!files || files?.length === 0) return;

    setIsUploading(true);
    try {
      const remainingSlots = Math.max(0, 5 - (formData?.image_urls?.length || 0));
      // Validação de tipo e tamanho (até 8MB por imagem)
      const MAX_SIZE_BYTES = 8 * 1024 * 1024;
      const allFiles = Array.from(files);
      const invalidFiles = allFiles.filter(f => !String(f?.type || '').startsWith('image/') || (f?.size || 0) > MAX_SIZE_BYTES);
      if (invalidFiles.length > 0) {
        toast.error('Alguns arquivos foram rejeitados: apenas imagens até 8MB.');
      }
      const validFiles = allFiles.filter(f => !invalidFiles.includes(f));
      const filesToUpload = validFiles.slice(0, remainingSlots);

      const uploadedUrls = [];
      for (const file of filesToUpload) {
        const targetId = editingPrinter?.id || user?.id;
        const { publicUrl } = await supabaseHelpers?.uploadPrinterImage(file, targetId);
        if (publicUrl) {
          uploadedUrls.push(publicUrl);
        } else {
          console.warn('Upload de imagem não retornou URL pública.');
        }
      }

      setFormData(prev => ({ ...prev, image_urls: [...(prev?.image_urls || []), ...uploadedUrls] }));
    } catch (error) {
      console.error('Erro ao fazer upload das imagens:', error);
      toast.error('Erro ao fazer upload das imagens. Tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData?.manufacturer_id) newErrors.manufacturer_id = 'Fabricante é obrigatório';
    if (!formData?.build_volume_x) newErrors.build_volume_x = 'Largura (X) é obrigatória';
    if (!formData?.build_volume_y) newErrors.build_volume_y = 'Profundidade (Y) é obrigatória';
    if (!formData?.build_volume_z) newErrors.build_volume_z = 'Altura (Z) é obrigatória';
    if (!formData?.power_consumption) newErrors.power_consumption = 'Consumo de energia é obrigatório';

    if (formData?.manufacturer_id === 'custom' && !formData?.custom_manufacturer?.trim()) {
      newErrors.custom_manufacturer = 'Informe o nome do fabricante';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    try {
      const selectedModelLabel = (() => {
        if (formData?.model_id && Array.isArray(models)) {
          const m = models?.find(mm => mm?.id === formData?.model_id || mm?.value === formData?.model_id);
          return m?.label || m?.name || null;
        }
        return null;
      })();

      const printerData = {
        ...formData,
        user_id: user?.id,
        model: formData?.custom_model || selectedModelLabel || '',
        brand: formData?.manufacturer_id === 'custom'
          ? (formData?.custom_manufacturer || 'Personalizado')
          : (manufacturers?.find(m => m?.value === formData?.manufacturer_id)?.label || 'Personalizado'),
        id: editingPrinter?.id || Date.now()?.toString(),
        build_volume: `${formData?.build_volume_x}×${formData?.build_volume_y}×${formData?.build_volume_z}mm`,
        totalHours: editingPrinter?.totalHours || 0,
        completedJobs: editingPrinter?.completedJobs || 0,
        lastActivity: editingPrinter?.lastActivity || 'Nunca usado',
        needsMaintenance: editingPrinter?.needsMaintenance || false,
        image: (formData?.image_urls?.[0]) || editingPrinter?.image,
        image_urls: formData?.image_urls || [],
        imageAlt: `Impressora 3D ${formData?.custom_model} em ambiente de trabalho profissional`,
        purchasePrice: (() => {
          const digits = String(formData?.purchase_price || '')?.replace(/\D/g, '');
          return Number(digits) / 100;
        })(),
        purchaseDate: formData?.purchase_date || '',
        supplier: formData?.supplier || '',
        hasAMS: !!formData?.has_ams,
        nozzleSize: formData?.nozzle_size === 'other'
          ? String(formData?.custom_nozzle_size || '')
          : (formData?.nozzle_size ? String(formData?.nozzle_size) : ''),
        filamentPrintedKg: formData?.filament_printed_kg ? Number(formData?.filament_printed_kg) : undefined,
        // Ajustes avançados numéricos
        totalFilamentBRL: (() => {
          const digits = String(formData?.filament_printed_brl || '')?.replace(/\D/g, '');
          return digits ? Number(digits) / 100 : undefined;
        })(),
        avgFilamentPerJobKg: formData?.avg_filament_per_job_kg ? Number(formData?.avg_filament_per_job_kg) : undefined,
        avgFilamentPerJobBRL: (() => {
          const digits = String(formData?.avg_filament_per_job_brl || '')?.replace(/\D/g, '');
          return digits ? Number(digits) / 100 : undefined;
        })(),
        failureRatePct: formData?.failure_rate_pct ? Number(formData?.failure_rate_pct) : undefined,
        materialWasteKg: formData?.material_waste_kg ? Number(formData?.material_waste_kg) : undefined,
        materialWasteBRL: (() => {
          const digits = String(formData?.material_waste_brl || '')?.replace(/\D/g, '');
          return digits ? Number(digits) / 100 : undefined;
        })(),
        grossProfit: (() => {
          const digits = String(formData?.gross_profit_brl || '')?.replace(/\D/g, '');
          return digits ? Number(digits) / 100 : undefined;
        })(),
        netProfit: (() => {
          const digits = String(formData?.net_profit_brl || '')?.replace(/\D/g, '');
          return digits ? Number(digits) / 100 : undefined;
        })()
      };

      await onSave(printerData);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar impressora:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-background border-l border-border z-50 overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                {editingPrinter ? 'Editar Impressora' : 'Nova Impressora'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Configure uma nova impressora 3D no seu sistema
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
            />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              <span>Informações Básicas</span>
            </h3>
            
            <Input
              label="Nome da Impressora"
              type="text"
              placeholder="Ex: Minha Ender 3 Principal"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Select
              label="Fabricante"
              placeholder="Selecione o fabricante"
              searchable
              options={manufacturers}
              value={formData?.manufacturer_id}
              onChange={(value) => {
                if (value === 'custom') { setIsAddBrandModalOpen(true); return; }
                handleInputChange('manufacturer_id', value);
              }}
              error={errors?.manufacturer_id}
              required
            />
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setIsAddBrandModalOpen(true)}
            >
              Não encontrou? Adicionar fabricante
            </button>

            {formData?.manufacturer_id === 'custom' && (
              <Input
                label="Nome do Fabricante"
                type="text"
                placeholder="Ex: Artillery"
                value={formData?.custom_manufacturer}
                onChange={(e) => handleInputChange('custom_manufacturer', e?.target?.value)}
                error={errors?.custom_manufacturer}
                required
              />
            )}

            {formData?.manufacturer_id && formData?.manufacturer_id !== 'custom' && (
              <div className="space-y-2">
                <Select
                  label="Modelo"
                  placeholder={isLoadingModels ? "Carregando modelos..." : "Selecione o modelo"}
                  searchable
                  options={models}
                  value={formData?.model_id}
                  onChange={(value) => {
                    if (value === 'custom') { setIsAddModelModalOpen(true); return; }
                    handleInputChange('model_id', value);
                  }}
                  disabled={isLoadingModels}
                />
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setIsAddModelModalOpen(true)}
                >
                  Não encontrou? Adicionar modelo
                </button>
              </div>
            )}

            {(formData?.manufacturer_id === 'custom' || formData?.model_id === 'custom') && (
              <Input
                label="Modelo Personalizado"
                type="text"
                placeholder="Ex: Ender 3 V2 Modificada"
                value={formData?.custom_model}
                onChange={(e) => handleInputChange('custom_model', e?.target?.value)}
                required
              />
            )}
          </div>

          {/* Volume de Impressão */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-secondary rounded-full"></span>
              <span>Volume de Impressão</span>
            </h3>

            {selectedModel && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                <strong>Modelo selecionado:</strong> {selectedModel?.name}<br/>
                <strong>Volume:</strong> {selectedModel?.build_volume_x}×{selectedModel?.build_volume_y}×{selectedModel?.build_volume_z}mm
              </div>
            )}

            <Input
              label="Volume (mm)"
              type="text"
              placeholder="L×P×A (ex: 220×220×250)"
              value={[formData?.build_volume_x, formData?.build_volume_y, formData?.build_volume_z]
                .filter(v => v !== '' && v !== undefined)
                .join('×')}
              onChange={(e) => handleVolumeCombinedChange(e?.target?.value)}
              error={errors?.build_volume_x || errors?.build_volume_y || errors?.build_volume_z}
              description="Informe largura × profundidade × altura em milímetros"
              required
            />
            <div className="grid grid-cols-3 gap-3">
              <Input
                label="Largura (X) (mm)"
                type="text"
                inputMode="numeric"
                placeholder="220"
                value={formData?.build_volume_x}
                onChange={(e) => handleInputChange('build_volume_x', e?.target?.value)}
                error={errors?.build_volume_x}
              />
              <Input
                label="Profundidade (Y) (mm)"
                type="text"
                inputMode="numeric"
                placeholder="220"
                value={formData?.build_volume_y}
                onChange={(e) => handleInputChange('build_volume_y', e?.target?.value)}
                error={errors?.build_volume_y}
              />
              <Input
                label="Altura (Z) (mm)"
                type="text"
                inputMode="numeric"
                placeholder="250"
                value={formData?.build_volume_z}
                onChange={(e) => handleInputChange('build_volume_z', e?.target?.value)}
                error={errors?.build_volume_z}
              />
            </div>
          </div>

        {/* Configurações Operacionais */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <span className="w-2 h-2 bg-accent rounded-full"></span>
            <span>Configurações</span>
          </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Consumo de Energia (W)"
                type="text"
                placeholder="220"
                value={formData?.power_consumption}
                onChange={(e) => handleInputChange('power_consumption', e?.target?.value)}
                error={errors?.power_consumption}
                description="Apenas números"
                required
              />
            </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Bico instalado (mm)"
            options={[
              ...[0.2,0.3,0.4,0.5,0.6,0.8,1.0].map(n => ({ value: String(n), label: `${n} mm` })),
              { value: 'other', label: 'Outro (digitar)' }
            ]}
            value={formData?.nozzle_size ? String(formData?.nozzle_size) : ''}
            onChange={(value) => handleInputChange('nozzle_size', value)}
            placeholder="Selecione"
          />

          {String(formData?.nozzle_size) === 'other' && (
            <Input
              label="Bico personalizado (mm)"
              type="text"
              inputMode="decimal"
              placeholder="Ex: 0.25"
              value={formData?.custom_nozzle_size}
              onChange={(e) => handleInputChange('custom_nozzle_size', e?.target?.value)}
              description="Informe o diâmetro do bico em milímetros"
            />
          )}

          <Select
            label="Status da Impressora"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => handleInputChange('status', value)}
          />
        </div>

        
          
        </div>

        {/* Ajustes Avançados */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <span className="w-2 h-2 bg-muted-foreground rounded-full"></span>
            <span>Ajustes Avançados</span>
          </h3>
          <p className="text-sm text-muted-foreground">Use para informar métricas manuais ao adicionar impressoras antigas.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Filamento Impresso Total (Kg)"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formData?.filament_printed_kg}
              onChange={(e) => handleInputChange('filament_printed_kg', e?.target?.value)}
              description="Kg total já impresso"
            />
            <Input
              label="Filamento Impresso Total (R$)"
              type="text"
              placeholder="R$ 0,00"
              value={formData?.filament_printed_brl}
              onChange={(e) => handleInputChange('filament_printed_brl', e?.target?.value)}
            />
            <Input
              label="Média de filamento por job (Kg)"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formData?.avg_filament_per_job_kg}
              onChange={(e) => handleInputChange('avg_filament_per_job_kg', e?.target?.value)}
            />
            <Input
              label="Média de filamento por job (R$)"
              type="text"
              placeholder="R$ 0,00"
              value={formData?.avg_filament_per_job_brl}
              onChange={(e) => handleInputChange('avg_filament_per_job_brl', e?.target?.value)}
            />
            <Input
              label="% de falhas em impressão"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formData?.failure_rate_pct}
              onChange={(e) => handleInputChange('failure_rate_pct', e?.target?.value)}
              description="Percentual (0–100)"
            />
            <Input
              label="Desperdício de material (Kg)"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={formData?.material_waste_kg}
              onChange={(e) => handleInputChange('material_waste_kg', e?.target?.value)}
            />
            <Input
              label="Desperdício de material (R$)"
              type="text"
              placeholder="R$ 0,00"
              value={formData?.material_waste_brl}
              onChange={(e) => handleInputChange('material_waste_brl', e?.target?.value)}
            />
            <Input
              label="Lucro Bruto"
              type="text"
              placeholder="R$ 0,00"
              value={formData?.gross_profit_brl}
              onChange={(e) => handleInputChange('gross_profit_brl', e?.target?.value)}
            />
            <Input
              label="Lucro Líquido"
              type="text"
              placeholder="R$ 0,00"
              value={formData?.net_profit_brl}
              onChange={(e) => handleInputChange('net_profit_brl', e?.target?.value)}
            />
          </div>
        </div>

        {/* Compra */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            <span>Compra</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Preço pago (R$)"
              type="text"
              placeholder="R$ 0,00"
              value={formData?.purchase_price}
              onChange={(e) => handleInputChange('purchase_price', e?.target?.value)}
            />

            <Input
              label="Data da compra"
              type="date"
              value={formData?.purchase_date}
              onChange={(e) => handleInputChange('purchase_date', e?.target?.value)}
            />
            <Select
              label="Fornecedor / Local de compra"
              searchable
              clearable
              options={[
                ...Array.from(new Set((globalSuppliers || []).map(s => String(s).trim())))
                  .sort((a,b)=>a.localeCompare(b))
                  .map(s => ({ value: s, label: s })),
                { value: '__add_supplier__', label: 'Adicionar fornecedor...', badge: { text: 'Novo', variant: 'success' } }
              ]}
              value={formData?.supplier}
              onChange={(value) => {
                if (value === '__add_supplier__') { setIsAddSupplierModalOpen(true); return; }
                handleInputChange('supplier', value);
              }}
            />
          </div>

          <Checkbox
            checked={!!formData?.has_ams}
            onChange={(e) => handleInputChange('has_ams', e?.target?.checked)}
            label="Possui AMS ou equivalente"
          />
        </div>

          {/* Upload de Imagens */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-success rounded-full"></span>
              <span>Imagens da Impressora</span>
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Upload Direto para Servidor (até 5 imagens)
                </label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 bg-muted/30">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = e?.target?.files;
                      if (files) handleFilesUpload(files);
                    }}
                    className="sr-only"
                    disabled={isUploading}
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">Até 5 imagens</div>
                    <Button
                      type="button"
                      onClick={() => fileInputRef?.current?.click()}
                      className="px-4"
                    >Selecionar imagens</Button>
                  </div>
                </div>
                {isUploading && (
                  <p className="text-sm text-primary mt-2 flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>Fazendo upload...</span>
                  </p>
                )}
              </div>
              {/* Gallery Preview */}
              {formData?.image_urls?.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {formData?.image_urls?.map((url, idx) => (
                    <div key={idx} className="relative w-full h-24 rounded-lg overflow-hidden border border-border bg-muted">
                      <img src={url} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        className="absolute top-2 right-2 px-2 py-1 text-xs bg-black/60 text-white rounded"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            image_urls: prev?.image_urls?.filter((_, i) => i !== idx)
                          }));
                        }}
                      >Remover</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Observações */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-warning rounded-full"></span>
              <span>Observações</span>
            </h3>

            <Input
              label="Notas"
              type="textarea"
              placeholder="Informações adicionais, modificações, configurações especiais..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="sticky bottom-0 bg-background border-t border-border p-6 -mx-6 -mb-6">
            <div className="flex items-center space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                iconName="Save"
                iconPosition="left"
                className="flex-1"
              >
                {editingPrinter ? 'Atualizar' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
      {/* Modal para adicionar fornecedor */}
      {isAddSupplierModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsAddSupplierModalOpen(false)} />
          <div className="relative bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Adicionar novo fornecedor</h3>
            <Input
              label="Nome do fornecedor"
              type="text"
              placeholder="Ex: Amazon, FilipeFlop..."
              value={newSupplierName}
              onChange={(e) => setNewSupplierName(e?.target?.value)}
            />
            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddSupplierModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={async () => {
                const name = String(newSupplierName || '').trim();
                if (!name) return;
                const { data: created } = await settingsStore.createSupplier(name);
                setNewSupplierName('');
                setIsAddSupplierModalOpen(false);
                const { data } = await settingsStore.getSuppliers();
                setGlobalSuppliers(Array.isArray(data) ? data : []);
                setFormData(prev => ({ ...prev, supplier: created || name }));
              }}>Adicionar</Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Modal para adicionar fabricante */}
      {isAddBrandModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsAddBrandModalOpen(false)} />
          <div className="relative bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Adicionar novo fabricante</h3>
            <Input
              label="Nome do fabricante"
              type="text"
              placeholder="Ex: Creality, Prusa..."
              value={newBrandName}
              onChange={(e) => setNewBrandName(e?.target?.value)}
            />
            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddBrandModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={async () => {
                const name = String(newBrandName || '').trim();
                if (!name) return;
                const { data: created } = await settingsStore.createPrinterBrand(name);
                setNewBrandName('');
                setIsAddBrandModalOpen(false);
                await loadManufacturers();
                const slug = String(created || name).toLowerCase().replace(/\s+/g, '_');
                setFormData(prev => ({ ...prev, manufacturer_id: slug }));
              }}>Adicionar</Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Modal para adicionar modelo */}
      {isAddModelModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsAddModelModalOpen(false)} />
          <div className="relative bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">Adicionar novo modelo</h3>
            <p className="text-xs text-muted-foreground mb-4">Fabricante atual: {manufacturers?.find(m => m?.value === formData?.manufacturer_id)?.label || '—'}</p>
            <Input
              label="Nome do modelo"
              type="text"
              placeholder="Ex: Ender 3 S1, X1 Carbon..."
              value={newModelName}
              onChange={(e) => setNewModelName(e?.target?.value)}
            />
            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddModelModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={async () => {
                const name = String(newModelName || '').trim();
                if (!name) return;
                const brandLabel = manufacturers?.find(m => m?.value === formData?.manufacturer_id)?.label || formData?.manufacturer_id;
                await settingsStore.createPrinterModelForBrand(brandLabel, name);
                setNewModelName('');
                setIsAddModelModalOpen(false);
                await loadModels(formData?.manufacturer_id);
                setFormData(prev => ({ ...prev, model_id: name, custom_model: name }));
              }}>Adicionar</Button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default AddPrinterDrawer;